import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { toast } from "sonner";
import { Sparkles, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";

const GRADES = ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "IGCSE", "A-Levels", "IB", "AP", "JEE", "NEET", "SAT", "Undergrad"].map((g) => ({ value: g, label: g }));

export default function SignupStudent() {
  const { signupStudent } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", grade: "", parent_email: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signupStudent(form);
      toast.success("Account created");
      nav("/student");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[440px] h-[440px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,180,0,0.30), transparent 60%)" }} />
          <div className="absolute -bottom-40 -right-16 w-[460px] h-[460px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.20), transparent 60%)" }} />
        </div>
        <div className="relative w-full max-w-md mx-auto px-6 py-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <GraduationCap size={12} /> Student signup
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Join your AI classroom.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">50 free AI tutor credits to get started.</p>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
            className="bg-white border border-black/8 rounded-2xl shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)] mt-8 p-6 sm:p-8 space-y-4"
          >
            <GlowField label="Full name" testid="student-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            <GlowField label="Email" testid="student-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
            <GlowField label="Password" testid="student-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
            <div>
              <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Grade / Class</div>
              <SearchableSelect
                testid="student-grade"
                placeholder="Select grade"
                options={GRADES}
                value={form.grade}
                onChange={(v) => set("grade", v)}
              />
            </div>
            <GlowField label="Parent email (optional)" type="email" value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} placeholder="parent@home.com" />
            <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="student-submit">
              <Sparkles size={14} /> {loading ? "Creating…" : "Create student account"}
            </CtaButton>
          </motion.form>

          <div className="mt-6 text-sm text-neutral-600">Already have an account? <Link to="/login" className="text-[#0A0C10] font-semibold underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Sign in</Link></div>
        </div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
